import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

export default function SettingsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    whole_tracking_query: '',
    updated_tracking_query: '',
    columns: '',
    query_batch_size: '',
    is_notify_customer: false,
    read_endpoint: ''
  });
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Fetch sync status
  const {
    data: syncStatusResponse,
    isLoading: syncStatusLoading
  } = useQuery({
    queryKey: ['trackingSyncStatus'],
    queryFn: async () => {
      const response = await api.get('/tracking-sync/status');
      return response.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 5000 // Poll every 5 seconds
  });

  const isSyncRunning = syncStatusResponse?.data?.isRunning || false;

  const {
    data: settingsData,
    isLoading: isLoadingSettings,
    error: settingsError
  } = useQuery({
    queryKey: ['trackingSyncStatus', 'trackingSyncSettingsStatus'],
    queryFn: async () => {
      const response = await api.get('/tracking-sync/settings');
      return response.data.data;
    },
    staleTime: 0
  });

  useEffect(() => {
    if (settingsData) {
      setSettings(settingsData);
    }
  }, [settingsData]);

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : (name === 'query_batch_size' ? (value === '' ? '' : parseInt(value)) : value)
    });
  };

  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const response = await api.put('/tracking-sync/settings', updatedSettings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['trackingSyncStatus', 'trackingSyncSettingsStatus'] });
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
    }
  });

  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.resetTrackingSyncSettings();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Settings reset to default successfully');
      queryClient.invalidateQueries({ queryKey: ['trackingSyncStatus', 'trackingSyncSettingsStatus'] });
      setShowResetDialog(false);
    },
    onError: (error) => {
      toast.error(`Failed to reset settings: ${error.response?.data?.message || error.message}`);
      setShowResetDialog(false);
    }
  });

  const handleSettingsSubmit = (e) => {
    e.preventDefault();

    // Convert numeric fields to proper numbers for submission
    const processedSettings = { ...settings };
    if (processedSettings.query_batch_size !== '') {
      processedSettings.query_batch_size = parseInt(processedSettings.query_batch_size);
    }

    updateSettingsMutation.mutate(processedSettings);
  };

  const handleResetSettings = () => {
    setShowResetDialog(true);
  };

  const handleConfirmReset = () => {
    resetSettingsMutation.mutate();
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {/* Sync in progress warning banner */}
        {isSyncRunning && isAdmin && (
          <Alert severity='warning' sx={{ mb: 2 }}>
          Tracking synchronization is currently in progress. Settings updates are disabled until the sync completes.
          </Alert>
        )}
        <Paper sx={{ p: '15px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={4}>
            <Box display='flex' alignItems='center'>
              <SettingsIcon color='primary' sx={{ mr: 1 }} />
              <Typography variant='h6'>
                Tracking Sync Settings
              </Typography>
            </Box>
            {isAdmin ? (
              <>
                <Button
                  variant='contained'
                  color='warning'
                  onClick={handleResetSettings}
                  disabled={!isAdmin || resetSettingsMutation.isPending || isSyncRunning || isLoadingSettings}
                  startIcon={<RestoreIcon />}
                >
                  Reset
                </Button>
              </>
            ) : (<></>)}
          </Box>

          {isLoadingSettings || syncStatusLoading ? (
            <Box display='flex' justifyContent='center' my={4}>
              <CircularProgress />
            </Box>
          ) : settingsError ? (
            <Alert severity='error' sx={{ mb: 3 }}>
              Error loading settings: {settingsError.message}
            </Alert>
          ) : !settingsData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <form onSubmit={handleSettingsSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant='subtitle1' gutterBottom fontWeight='medium'>
                    ERP Query Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Whole Tracking Query'
                    name='whole_tracking_query'
                    value={settings.whole_tracking_query}
                    onChange={handleSettingsChange}
                    fullWidth
                    multiline
                    rows={4}
                    variant='outlined'
                    disabled={!isAdmin || isSyncRunning}
                    helperText={!isAdmin ? 'Only admins can change query settings' : 'SQL query to fetch all tracking data from ERP for complete sync'}
                    sx={{ mb: 2 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Updated Tracking Query'
                    name='updated_tracking_query'
                    value={settings.updated_tracking_query}
                    onChange={handleSettingsChange}
                    fullWidth
                    multiline
                    rows={4}
                    variant='outlined'
                    disabled={!isAdmin || isSyncRunning}
                    helperText={!isAdmin ? 'Only admins can change query settings' : 'SQL query to fetch only updated tracking data from ERP for incremental sync'}
                    sx={{ mb: 2 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Columns'
                    name='columns'
                    value={settings.columns}
                    onChange={handleSettingsChange}
                    fullWidth
                    multiline
                    rows={3}
                    variant='outlined'
                    disabled={!isAdmin || isSyncRunning}
                    helperText={!isAdmin ? 'Only admins can change column settings' : 'Comma-separated list of columns to select from the query'}
                    sx={{ mb: 2 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Read Endpoint'
                    name='read_endpoint'
                    value={settings.read_endpoint}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant='outlined'
                    disabled={!isAdmin || isSyncRunning}
                    placeholder='/api/data/read'
                    helperText={!isAdmin ? 'Only admins can change endpoint settings' : 'ERP API endpoint path for reading tracking data (e.g., /api/data/read)'}
                    sx={{ mb: 2 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='subtitle1' gutterBottom fontWeight='medium'>
                    Sync Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label='Query Batch Size'
                    name='query_batch_size'
                    type='number'
                    value={settings.query_batch_size}
                    onChange={handleSettingsChange}
                    fullWidth
                    variant='outlined'
                    disabled={!isAdmin || isSyncRunning}
                    inputProps={{ min: 1, max: 1000 }}
                    helperText='Number of records to process in each batch (1-1000)'
                    sx={{ mb: 2 }}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.is_notify_customer}
                        onChange={handleSettingsChange}
                        name='is_notify_customer'
                        color='primary'
                        disabled={!isAdmin || isSyncRunning}
                      />
                    }
                    label='Notify Customer'
                    sx={{ mt: 2 }}
                  />
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    Send tracking notifications to customers via Shopify
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Box display='flex' justifyContent='end' alignItems='center'>
                    {isAdmin ? (
                      <>
                        <Button
                          type='submit'
                          variant='contained'
                          color='primary'
                          disabled={!isAdmin || updateSettingsMutation.isPending || isSyncRunning}
                          startIcon={updateSettingsMutation.isPending ?
                            <CircularProgress size={20} color='inherit' /> :
                            <SaveIcon />
                          }
                          size='large'
                        >
                          {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                        </Button>
                      </>
                    ) : (<></>)}
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Grid>

      <ConfirmDialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleConfirmReset}
        confirmColor='warning'
        title='Reset Settings to Default'
        message='Are you sure you want to reset all tracking sync settings to their default values? This action cannot be undone.'
        confirmText='Reset'
        cancelText='Cancel'
        severity='warning'
        isLoading={resetSettingsMutation.isPending}
      />
    </Grid>
  );
}
